/**
 * Продуктовые метрики: люди, заходы, игры, воронка и источники.
 */
import { useMemo, useState } from "react";
import { Alert, Card, Col, DatePicker, Progress, Row, Segmented, Space, Spin, Statistic, Table, Tooltip, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { Link } from "react-router";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ChartCard } from "./chart";
import { COLORS, axisProps, fmt, gridProps, percent, tooltipProps } from "./chartTheme";
import { type MetricsOverview, num } from "./types";
import { useMetrics } from "./useMetrics";

const PRESETS = [
  { label: "7 дней", value: 7 },
  { label: "30 дней", value: 30 },
  { label: "90 дней", value: 90 },
  { label: "Год", value: 365 },
];

const SOURCE_NAMES: Record<string, string> = {
  direct: "Прямые заходы",
  google: "Google",
  yandex: "Яндекс",
  telegram: "Telegram",
  vk: "ВКонтакте",
  bing: "Bing",
  duckduckgo: "DuckDuckGo",
  reddit: "Reddit",
  x: "X (Twitter)",
  facebook: "Facebook",
  youtube: "YouTube",
};

export function ProductTab() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(29, "day"), dayjs()]);
  const [preset, setPreset] = useState<number | null>(30);

  const { data, error, loading } = useMetrics<MetricsOverview>(
    "/api/v1/admin/metrics/overview",
    () => ({ from: range[0].format("YYYY-MM-DD"), to: range[1].format("YYYY-MM-DD") }),
    [range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")],
    // Сводка меняется медленно — раз в пять минут достаточно.
    5 * 60_000,
  );

  const daily = useMemo(
    () =>
      (data?.daily ?? []).map((d) => ({
        date: dayjs(d.date).format("D MMM"),
        signups: num(d.signups),
        visitors: num(d.uniqueVisitors),
        visits: num(d.visits),
        started: num(d.gamesStarted),
        finished: num(d.gamesFinished),
        guesses: num(d.guesses),
        players: num(d.activePlayers),
        deletions: num(d.deletionRequests),
      })),
    [data],
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Segmented
          options={PRESETS}
          value={preset ?? undefined}
          onChange={(days) => {
            setPreset(days as number);
            setRange([dayjs().subtract((days as number) - 1, "day"), dayjs()]);
          }}
        />
        <DatePicker.RangePicker
          value={range}
          allowClear={false}
          format="D MMM YYYY"
          disabledDate={(day) => day.isAfter(dayjs(), "day")}
          onChange={(value) => {
            if (value?.[0] && value[1]) {
              setPreset(null);
              setRange([value[0], value[1]]);
            }
          }}
        />
        {loading && <Spin size="small" />}
        {data && (
          <Typography.Text type="secondary">Сутки считаются по поясу {data.timezone}</Typography.Text>
        )}
      </Space>

      {error && <Alert type="error" showIcon message="Метрики не загрузились" description={error} />}

      {data && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} xl={6}>
              <Card size="small" title="Пользователи">
                <Statistic title="Зарегистрировано всего" value={num(data.users.total)} formatter={(v) => fmt(Number(v))} />
                <Space size="large" style={{ marginTop: 12 }}>
                  <Statistic title="Новых за период" value={num(data.users.newInPeriod)} valueStyle={{ fontSize: 18 }} />
                  <Statistic
                    title="Подтвердили почту"
                    value={percent(num(data.users.verified), num(data.users.total))}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card size="small" title="Посетители сайта">
                <Statistic title="Уникальных сегодня" value={num(data.visits.uniqueToday)} formatter={(v) => fmt(Number(v))} />
                <Space size="large" style={{ marginTop: 12 }}>
                  <Tooltip title="Посетитель различим только в пределах суток, поэтому за период это сумма уникальных по дням">
                    <Statistic title="Посетитель-дней" value={num(data.visits.uniqueVisitors)} valueStyle={{ fontSize: 18 }} />
                  </Tooltip>
                  <Statistic title="Заходов" value={num(data.visits.visits)} valueStyle={{ fontSize: 18 }} />
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card size="small" title="Активные игроки">
                <Tooltip title="Сделали хотя бы один ответ за последние сутки">
                  <Statistic title="За сутки (DAU)" value={num(data.activity.dau)} />
                </Tooltip>
                <Space size="large" style={{ marginTop: 12 }}>
                  <Statistic title="За неделю" value={num(data.activity.wau)} valueStyle={{ fontSize: 18 }} />
                  <Statistic title="За месяц" value={num(data.activity.mau)} valueStyle={{ fontSize: 18 }} />
                  <Tooltip title="DAU / MAU: какая доля игроков месяца приходит каждый день">
                    <Statistic
                      title="Залипание"
                      value={percent(num(data.activity.dau), num(data.activity.mau))}
                      valueStyle={{ fontSize: 18 }}
                    />
                  </Tooltip>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card size="small" title="Партии за период">
                <Statistic title="Начато" value={num(data.games.started)} formatter={(v) => fmt(Number(v))} />
                <Space size="large" style={{ marginTop: 12 }}>
                  <Statistic
                    title="Доиграно"
                    value={percent(num(data.games.finished), num(data.games.started))}
                    valueStyle={{ fontSize: 18 }}
                  />
                  <Statistic
                    title="С друзьями"
                    value={percent(num(data.games.multiplayer), num(data.games.started))}
                    valueStyle={{ fontSize: 18 }}
                  />
                  <Statistic title="Ответов" value={num(data.games.guesses)} valueStyle={{ fontSize: 18 }} />
                </Space>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} xl={12}>
              <ChartCard title="Посетители и заходы по дням">
                <AreaChart data={daily}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...axisProps} />
                  <YAxis allowDecimals={false} {...axisProps} width={40} />
                  <ChartTooltip {...tooltipProps} />
                  <Legend />
                  <Area type="monotone" dataKey="visits" name="Заходы" stroke={COLORS.blue} fill={COLORS.blue} fillOpacity={0.15} />
                  <Area type="monotone" dataKey="visitors" name="Уникальные посетители" stroke={COLORS.amber} fill={COLORS.amber} fillOpacity={0.3} />
                </AreaChart>
              </ChartCard>
            </Col>
            <Col xs={24} xl={12}>
              <ChartCard title="Регистрации и активные игроки">
                <LineChart data={daily}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...axisProps} />
                  <YAxis allowDecimals={false} {...axisProps} width={40} />
                  <ChartTooltip {...tooltipProps} />
                  <Legend />
                  <Line type="monotone" dataKey="signups" name="Регистрации" stroke={COLORS.amber} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="players" name="Играли" stroke={COLORS.green} strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col xs={24} xl={12}>
              <ChartCard title="Партии по дням">
                <BarChart data={daily}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...axisProps} />
                  <YAxis allowDecimals={false} {...axisProps} width={40} />
                  <ChartTooltip {...tooltipProps} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Legend />
                  <Bar dataKey="started" name="Начато" fill={COLORS.blue} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="finished" name="Доиграно" fill={COLORS.green} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ChartCard>
            </Col>
            <Col xs={24} xl={12}>
              <Funnel data={data} />
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} xl={14}>
              <Card size="small" title="Популярные квесты за период">
                <Table
                  size="small"
                  rowKey="questId"
                  pagination={false}
                  dataSource={data.topQuests}
                  locale={{ emptyText: "За период партий не было" }}
                  columns={[
                    {
                      title: "Квест",
                      dataIndex: "title",
                      render: (title: string, row) => <Link to={`/quests/show/${row.questId}`}>{title}</Link>,
                    },
                    { title: "Партий", dataIndex: "games", align: "right", render: (v) => fmt(num(v)) },
                    {
                      title: "Доиграно",
                      align: "right",
                      render: (_, row) => percent(num(row.finished), num(row.games)),
                    },
                    { title: "Игроков", dataIndex: "players", align: "right", render: (v) => fmt(num(v)) },
                    { title: "Средний счёт", dataIndex: "avgScore", align: "right", render: (v: number) => fmt(Math.round(v)) },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} xl={10}>
              <Sources data={data} />
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card size="small" title="Контент и модерация">
                <Space size={48} wrap>
                  <Statistic title="Опубликовано фотографий" value={num(data.content.publishedPhotos)} formatter={(v) => fmt(Number(v))} />
                  <Statistic title="Опубликовано квестов" value={num(data.content.publishedQuests)} />
                  <Statistic
                    title={<Link to="/quests">Квестов игроков ждут проверки</Link>}
                    value={num(data.content.pendingQuests)}
                    valueStyle={num(data.content.pendingQuests) ? { color: COLORS.amber } : undefined}
                  />
                  <Statistic
                    title={<Link to="/feedback">Неразобранных сообщений</Link>}
                    value={num(data.content.newFeedback)}
                    valueStyle={num(data.content.newFeedback) ? { color: COLORS.amber } : undefined}
                  />
                  <Statistic title="Комнат создано за период" value={num(data.games.roomsCreated)} />
                  <Statistic title="Игроков в партии с друзьями" value={data.games.avgMultiplayerPlayers} precision={1} />
                  <Statistic title="Средний балл за ответ" value={data.games.avgGuessScore} precision={0} />
                </Space>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} xl={10}>
              <Card size="small" title="Удаление аккаунтов" style={{ height: "100%" }}>
                <Space size={40} wrap>
                  <Tooltip title="Сколько людей попросили удалить аккаунт за период">
                    <Statistic title="Запросов за период" value={num(data.deletions.requested)} />
                  </Tooltip>
                  <Statistic title="Передумали" value={num(data.deletions.canceled)} />
                  <Statistic title="Удалено" value={num(data.deletions.completed)} />
                  <Tooltip title="Ждут удаления прямо сейчас — таймер у каждого в разделе «Пользователи»">
                    <Statistic
                      title={<Link to="/users">Ждут удаления</Link>}
                      value={num(data.deletions.pending)}
                      valueStyle={num(data.deletions.pending) ? { color: COLORS.amber } : undefined}
                    />
                  </Tooltip>
                </Space>
                <Typography.Text type="secondary" style={{ display: "block", marginTop: 12, fontSize: 12 }}>
                  Доля передумавших: {percent(num(data.deletions.canceled), num(data.deletions.requested))}
                </Typography.Text>
              </Card>
            </Col>
            <Col xs={24} xl={14}>
              <ChartCard title="Запросы на удаление по дням" height={180}>
                <BarChart data={daily}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...axisProps} />
                  <YAxis allowDecimals={false} {...axisProps} width={40} />
                  <ChartTooltip {...tooltipProps} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="deletions" name="Запросов" fill={COLORS.red} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ChartCard>
            </Col>
          </Row>
        </>
      )}
    </Space>
  );
}

/**
 * Воронка периода. Ширина полос — от числа посетителей; подпись справа —
 * конверсия из предыдущего шага, по ней видно, где теряются люди.
 */
function Funnel({ data }: { data: MetricsOverview }) {
  const steps = [
    { label: "Посетители", value: num(data.funnel.visitors) },
    { label: "Зарегистрировались", value: num(data.funnel.signups) },
    { label: "Подтвердили почту", value: num(data.funnel.verified) },
    { label: "Сыграли хотя бы раунд", value: num(data.funnel.played) },
  ];
  // Ширина — от самого большого шага, а не от посетителей: их начали
  // считать позже регистраций, и при нуле посетителей полосы уходили в 100%.
  const top = Math.max(...steps.map((step) => step.value), 1);

  return (
    <Card size="small" title="Воронка за период" style={{ height: "100%" }}>
      <Space direction="vertical" style={{ width: "100%" }} size={14}>
        {steps.map((step, i) => (
          <div key={step.label}>
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Typography.Text>{step.label}</Typography.Text>
              <Typography.Text strong>
                {fmt(step.value)}
                {i > 0 && (
                  <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                    {percent(step.value, steps[i - 1].value)} от предыдущего
                  </Typography.Text>
                )}
              </Typography.Text>
            </Space>
            <Progress
              percent={Math.min(100, (step.value / top) * 100)}
              showInfo={false}
              strokeColor={COLORS.amber}
              trailColor={COLORS.grid}
            />
          </div>
        ))}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Шаги после посетителей — про людей, зарегистрировавшихся в этом периоде.
        </Typography.Text>
      </Space>
    </Card>
  );
}

function Sources({ data }: { data: MetricsOverview }) {
  const total = data.sources.reduce((sum, s) => sum + num(s.visitors), 0);
  return (
    <Card size="small" title="Откуда приходят" style={{ height: "100%" }}>
      <Table
        size="small"
        rowKey="source"
        pagination={false}
        dataSource={data.sources}
        locale={{ emptyText: "Заходов пока не было" }}
        columns={[
          { title: "Источник", dataIndex: "source", render: (s: string) => SOURCE_NAMES[s] ?? s },
          { title: "Посетителей", dataIndex: "visitors", align: "right", render: (v) => fmt(num(v)) },
          { title: "Доля", align: "right", render: (_, row) => percent(num(row.visitors), total) },
        ]}
      />
    </Card>
  );
}
